import React from 'react';

/**
 * PlanCard Component
 * Displays a subscription plan with features and pricing
 */
const PlanCard = ({
	plan,
	billingInterval = 'monthly',
	isCurrentPlan = false,
	isExactMatch = false,
	isLoading = false,
	onSelect,
	className = ''
}) => {
	const { name, description, features, priceIds } = plan;

	// Get price ID for the selected billing interval
	const priceId = priceIds[billingInterval];

	// Format feature limits
	const formatLimit = (limit) => {
		if (limit === -1) return 'Unlimited';
		return limit;
	};

	// Format export formats
	const formatExportFormats = (formats) => {
		return formats.join(', ');
	};

	const handleSelect = () => {
		if (onSelect && priceId) {
			onSelect(priceId, name);
		}
	};

	return (
		<div
			className={`relative rounded-lg border-2 p-6 transition-all ${
				isExactMatch
					? 'border-green-500 bg-green-50 shadow-lg'
					: isCurrentPlan
					? 'border-blue-500 bg-blue-50 shadow-lg'
					: 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
			} ${className}`}
		>
			{isExactMatch && (
				<div className="absolute -top-3 left-1/2 -translate-x-1/2">
					<span className="rounded-full bg-green-500 px-3 py-1 text-xs font-semibold text-white">
						✓ Active Plan
					</span>
				</div>
			)}
			{isCurrentPlan && !isExactMatch && (
				<div className="absolute -top-3 left-1/2 -translate-x-1/2">
					<span className="rounded-full bg-blue-500 px-3 py-1 text-xs font-semibold text-white">
						Your Tier
					</span>
				</div>
			)}

			<div className="mb-4">
				<h3 className="text-2xl font-bold text-gray-900">{name}</h3>
				<p className="mt-2 text-sm text-gray-600">{description}</p>
			</div>

			<div className="mb-6 space-y-3">
				<div className="flex items-center text-sm">
					<span className="font-medium text-gray-700">Documents:</span>
					<span className="ml-2 text-gray-900">
						{formatLimit(features.maxDocuments)}
					</span>
				</div>

				<div className="flex items-center text-sm">
					<span className="font-medium text-gray-700">Citations per Document:</span>
					<span className="ml-2 text-gray-900">
						{formatLimit(features.maxCitationsPerDocument)}
					</span>
				</div>

				<div className="flex items-center text-sm">
					<span className="font-medium text-gray-700">Export Formats:</span>
					<span className="ml-2 text-gray-900">
						{formatExportFormats(features.allowedExportFormats)}
					</span>
				</div>

				<div className="flex items-center text-sm">
					<span className="font-medium text-gray-700">Watermark:</span>
					<span className="ml-2 text-gray-900">
						{features.hasWatermark ? 'Yes' : 'No'}
					</span>
				</div>

				{features.canUseAdvancedFeatures && (
					<div className="flex items-center text-sm text-green-600">
						<svg
							className="mr-2 h-5 w-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M5 13l4 4L19 7"
							/>
						</svg>
						Advanced Features
					</div>
				)}

				{features.canUseAISuggestions && (
					<div className="flex items-center text-sm text-green-600">
						<svg
							className="mr-2 h-5 w-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M5 13l4 4L19 7"
							/>
						</svg>
						AI Suggestions
					</div>
				)}

				{features.canUseTemplates && (
					<div className="flex items-center text-sm text-green-600">
						<svg
							className="mr-2 h-5 w-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M5 13l4 4L19 7"
							/>
						</svg>
						Templates
					</div>
				)}
			</div>

		<button
			onClick={handleSelect}
			disabled={isLoading || !priceId || isExactMatch}
			className={`w-full rounded-lg px-4 py-2 font-semibold transition-colors ${
				isExactMatch
					? 'bg-green-100 text-green-700 border-2 border-green-500 cursor-not-allowed'
					: isCurrentPlan
					? 'bg-yellow-500 text-white hover:bg-yellow-600'
					: isLoading
					? 'bg-gray-300 text-gray-500 cursor-wait'
					: 'bg-blue-600 text-white hover:bg-blue-700'
			}`}
		>
			{isLoading
				? 'Processing...'
				: isExactMatch
				? '✓ Current Plan'
				: isCurrentPlan
				? 'Change Billing'
				: 'Select Plan'}
		</button>
		</div>
	);
};

export default PlanCard;

